import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Paper,
  IconButton
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Launch as LaunchIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { publicPaymentAPI } from '../services/tripayService';
import { TripayPayment } from '../types/tripay';

const PaymentStatusPage: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  
  const [payment, setPayment] = useState<TripayPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (reference) {
      fetchPaymentStatus();
    }
  }, [reference]);

  const fetchPaymentStatus = async () => {
    if (!reference) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await publicPaymentAPI.getPaymentDetail(reference);
      if (response.success) {
        setPayment(response.data);
      } else {
        setError(response.message || 'Pembayaran tidak ditemukan');
      }
    } catch (err: any) {
      console.error('Error fetching payment status:', err);
      setError('Gagal memuat status pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'UNPAID':
        return 'warning';
      case 'EXPIRED':
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PAID':
        return <CheckCircleIcon />;
      case 'UNPAID':
        return <ScheduleIcon />;
      case 'EXPIRED':
      case 'FAILED':
        return <ErrorIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const handleCopyReference = () => {
    if (payment?.reference) {
      navigator.clipboard.writeText(payment.reference);
    }
  };

  const handleRefresh = () => {
    fetchPaymentStatus();
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          variant="outlined"
        >
          Kembali
        </Button>
      </Container>
    );
  }

  if (!payment) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Pembayaran tidak ditemukan
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Kembali
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Status Pembayaran
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Lacak status pembayaran Anda
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getStatusIcon(payment.status)}
              <Typography variant="h5">
                Detail Pembayaran
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={payment.status}
                color={getStatusColor(payment.status) as any}
                size="medium"
              />
              <IconButton onClick={handleRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Referensi Pembayaran
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontFamily="monospace">
                  {payment.reference}
                </Typography>
                <IconButton size="small" onClick={handleCopyReference}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary">
                Tagihan
              </Typography>
              <Typography variant="body1">
                {payment.bill?.bill_number || 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Customer
              </Typography>
              <Typography variant="body1">
                {payment.customer_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {payment.customer_email}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Metode Pembayaran
              </Typography>
              <Typography variant="body1">
                {payment.payment_method_name}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Jumlah
              </Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(payment.amount)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Berlaku hingga
              </Typography>
              <Typography variant="body1">
                {new Date(payment.expired_time).toLocaleString('id-ID')}
              </Typography>
            </Box>

            {payment.paid_time && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Waktu Pembayaran
                </Typography>
                <Typography variant="body1">
                  {new Date(payment.paid_time).toLocaleString('id-ID')}
                </Typography>
              </Box>
            )}
          </Stack>

          {payment.status === 'UNPAID' && payment.checkout_url && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" gutterBottom>
                  Belum melakukan pembayaran?
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LaunchIcon />}
                  onClick={() => window.open(payment.checkout_url, '_blank')}
                >
                  Bayar Sekarang
                </Button>
              </Box>
            </>
          )}

          {payment.status === 'PAID' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Alert severity="success">
                <Typography variant="body2">
                  Pembayaran berhasil! Terima kasih telah melakukan pembayaran.
                </Typography>
              </Alert>
            </>
          )}

          {(payment.status === 'EXPIRED' || payment.status === 'FAILED') && (
            <>
              <Divider sx={{ my: 3 }} />
              <Alert severity="error">
                <Typography variant="body2">
                  Pembayaran {payment.status === 'EXPIRED' ? 'telah kedaluwarsa' : 'gagal'}. 
                  Silakan hubungi customer service untuk bantuan.
                </Typography>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentStatusPage;
